import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertPublicRateLimit } from "@/lib/rate-limit.server";

// Inquiries addressed to Reality Twin itself. These must reach the RTN team
// inbox — inserting them into the *sender's* lead list (previous behaviour)
// meant nobody at RTN ever saw them.

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

async function forwardToSales(input: {
  kind: string;
  name: string;
  email: string;
  company?: string;
  message: string;
}): Promise<void> {
  const { enqueueTransactionalEmail, baseEmailTemplate, SALES_INBOX } =
    await import("@/lib/notify.server");
  await enqueueTransactionalEmail({
    to: SALES_INBOX,
    subject: `[RTN ${input.kind}] ${input.name}${input.company ? ` — ${input.company}` : ""}`,
    html: baseEmailTemplate(
      `${input.kind} inquiry`,
      `<p><strong>${escapeHtml(input.name)}</strong> (${escapeHtml(input.email)})${
        input.company ? ` from <strong>${escapeHtml(input.company)}</strong>` : ""
      } wrote:</p><p style="margin-top:12px;padding:12px 16px;background:#15151c;border-radius:10px;white-space:pre-wrap">${escapeHtml(input.message)}</p><p style="margin-top:12px">Reply directly to ${escapeHtml(input.email)}.</p>`,
    ),
    text: `${input.kind} inquiry from ${input.name} (${input.email})${input.company ? `, ${input.company}` : ""}:\n\n${input.message}`,
    label: "sales_inquiry",
  });
}

export const submitEnterpriseInquiry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        name: z.string().min(1).max(120),
        email: z.string().email(),
        company: z.string().min(1).max(120),
        message: z.string().min(10).max(2000),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await forwardToSales({ kind: "Enterprise", ...data });
    return { ok: true };
  });

// Public marketing contact form — no auth. Honeypot + length caps as abuse
// guard; delivery goes through the transactional email queue.
export const submitContactInquiry = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        name: z.string().trim().min(1).max(120),
        email: z.string().trim().email().max(200),
        company: z.string().trim().max(120).optional(),
        team: z.string().trim().max(80).optional(),
        message: z.string().trim().min(10).max(2000),
        // Honeypot: real users never fill this hidden field.
        website: z.string().max(0).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await assertPublicRateLimit("contact:submit", 5);
    await forwardToSales({
      kind: "Contact",
      name: data.name,
      email: data.email,
      company: data.company,
      message: `${data.team ? `Team size: ${data.team}\n\n` : ""}${data.message}`,
    });
    return { ok: true };
  });
